import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { ArtWeaverLogo } from './ArtWeaverButtons';

const CANVAS_WIDTH = 800;
const CANVAS_HEIGHT = 600;
const MAX_HISTORY = 15; // undo steps kept; each snapshot is ~2MB of pixels
const SIZES = Array.from({ length: 40 }, (_, index) => index + 1); // the slider's 1-40, one by one
const OPACITIES = Array.from({ length: 10 }, (_, index) => 100 - index * 10); // 100 down to 10, as the slider steps

// Colours panel: a row of greys (white to black), then five rows of hues, light to dark.
const GREYS = Array.from({ length: 12 }, (_, index) => {
  const level = Math.round(255 * (1 - index / 11)).toString(16).padStart(2, '0');
  return `#${level}${level}${level}`;
});
const HUES = [0, 25, 45, 60, 100, 150, 180, 205, 225, 265, 295, 330];
const SWATCHES = [...GREYS, ...[80, 66, 52, 40, 28].flatMap((lightness) => HUES.map((hue) => `hsl(${hue}, 85%, ${lightness}%)`))];
const DEFAULT_COLOR = '#000000';

const TOOLS = [
  { id: 'pencil', label: 'Pencil', icon: '✏️' },
  { id: 'eraser', label: 'Eraser', icon: '🧽' },
];

const TOOL_BUTTON =
  'h-9 w-9 shrink-0 rounded-lg border flex items-center justify-center text-base transition-colors disabled:opacity-40 disabled:cursor-not-allowed';

/**
 * An Artweaver-style drawing board: pencil and eraser, undo/clear, a colour grid
 * and brush size/opacity. The desktop app can't be embedded in a web page, so
 * this is a canvas made to look like it.
 *
 * The parent reads the picture through its ref: `exportImage()` returns a PNG
 * data URL, or null while the sheet is still blank.
 */
const ArtDrawingBoard = forwardRef(function ArtDrawingBoard(_props, ref) {
  const canvasRef = useRef(null);
  // Snapshots of the sheet, newest last: { image: ImageData, blank }. The last one
  // always matches what is on the canvas, so a stroke starts from it and undo
  // just puts the one before it back.
  const historyRef = useRef([]);
  const strokeRef = useRef(null); // { base: ImageData, points: [{ x, y }] } while drawing

  const [tool, setTool] = useState('pencil');
  const [color, setColor] = useState(DEFAULT_COLOR);
  const [size, setSize] = useState(6);
  const [opacity, setOpacity] = useState(100);
  const [historyLength, setHistoryLength] = useState(1);

  const getContext = () => canvasRef.current?.getContext('2d', { willReadFrequently: true });

  const pushHistory = (entry) => {
    const history = historyRef.current;
    history.push(entry);
    if (history.length > MAX_HISTORY) history.shift();
    setHistoryLength(history.length);
  };

  const fillWhite = (ctx) => {
    ctx.globalAlpha = 1;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
  };

  useEffect(() => {
    const ctx = getContext();
    if (!ctx) return;
    fillWhite(ctx);
    historyRef.current = [];
    pushHistory({ image: ctx.getImageData(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT), blank: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useImperativeHandle(ref, () => ({
    exportImage: () => {
      const latest = historyRef.current[historyRef.current.length - 1];
      if (!latest || latest.blank) return null;
      return canvasRef.current.toDataURL('image/png');
    },
  }));

  const pointFrom = (event) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return {
      x: ((event.clientX - rect.left) * CANVAS_WIDTH) / (rect.width || CANVAS_WIDTH),
      y: ((event.clientY - rect.top) * CANVAS_HEIGHT) / (rect.height || CANVAS_HEIGHT),
    };
  };

  // The whole stroke is redrawn over the sheet it started from on every move, so
  // a translucent stroke stays one even tone where it crosses itself.
  const paintStroke = (ctx, points) => {
    const paint = tool === 'eraser' ? '#ffffff' : color;
    ctx.globalAlpha = tool === 'eraser' ? 1 : opacity / 100;
    ctx.strokeStyle = paint;
    ctx.fillStyle = paint;
    ctx.lineWidth = size;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    if (points.length === 1) {
      ctx.arc(points[0].x, points[0].y, size / 2, 0, Math.PI * 2);
      ctx.fill();
    } else {
      ctx.moveTo(points[0].x, points[0].y);
      points.slice(1).forEach((point) => ctx.lineTo(point.x, point.y));
      ctx.stroke();
    }
    ctx.globalAlpha = 1;
  };

  const handlePointerDown = (event) => {
    const ctx = getContext();
    const latest = historyRef.current[historyRef.current.length - 1];
    if (!ctx || !latest) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    strokeRef.current = { base: latest.image, points: [pointFrom(event)] };
    paintStroke(ctx, strokeRef.current.points);
  };

  const handlePointerMove = (event) => {
    const stroke = strokeRef.current;
    if (!stroke) return;
    const ctx = getContext();
    stroke.points.push(pointFrom(event));
    ctx.putImageData(stroke.base, 0, 0);
    paintStroke(ctx, stroke.points);
  };

  const handlePointerEnd = () => {
    if (!strokeRef.current) return;
    strokeRef.current = null;
    pushHistory({ image: getContext().getImageData(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT), blank: false });
  };

  const handleUndo = () => {
    const history = historyRef.current;
    if (history.length <= 1) return;
    history.pop();
    getContext()?.putImageData(history[history.length - 1].image, 0, 0);
    setHistoryLength(history.length);
  };

  const handleClear = () => {
    const ctx = getContext();
    const latest = historyRef.current[historyRef.current.length - 1];
    if (!ctx || !latest || latest.blank) return;
    fillWhite(ctx);
    pushHistory({ image: ctx.getImageData(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT), blank: true });
  };

  const pickColor = (swatch) => {
    setColor(swatch);
    setTool('pencil'); // choosing a colour means you want to draw with it
  };

  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
      <div className="flex items-center gap-2 border-b border-gray-200 px-3 py-2">
        <ArtWeaverLogo />
        <span className="text-sm font-extrabold text-slate-700">Artweaver Free</span>
      </div>

      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 border-b border-gray-200 bg-gray-50 px-3 py-2 text-xs font-semibold text-gray-600">
        <label className="flex items-center gap-1.5">
          Size
          <select
            value={size}
            onChange={(event) => setSize(Number(event.target.value))}
            aria-label="Brush size"
            className="rounded border border-gray-300 bg-white px-1.5 py-0.5 text-slate-800"
          >
            {SIZES.map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-1.5">
          Opacity
          <select
            value={opacity}
            onChange={(event) => setOpacity(Number(event.target.value))}
            aria-label="Brush opacity"
            className="rounded border border-gray-300 bg-white px-1.5 py-0.5 text-slate-800"
          >
            {OPACITIES.map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>
        </label>
        <span className="flex items-center gap-1.5">
          Brush
          <span className="rounded border border-gray-300 bg-white px-2 py-0.5 text-slate-800">
            {tool === 'eraser' ? 'Eraser' : 'Pencil'}
          </span>
        </span>
      </div>

      <div className="flex flex-col [@container_(min-width:720px)]:flex-row">
        <div className="flex gap-1.5 border-b border-gray-200 bg-gray-50 p-2 [@container_(min-width:720px)]:flex-col [@container_(min-width:720px)]:border-b-0 [@container_(min-width:720px)]:border-r">
          {TOOLS.map(({ id, label, icon }) => (
            <button
              key={id}
              type="button"
              onClick={() => setTool(id)}
              aria-label={label}
              aria-pressed={tool === id}
              title={label}
              className={`${TOOL_BUTTON} ${
                tool === id ? 'border-pink-400 bg-pink-100' : 'border-gray-200 bg-white hover:bg-gray-100'
              }`}
            >
              <span aria-hidden="true">{icon}</span>
            </button>
          ))}
          <button
            type="button"
            onClick={handleUndo}
            disabled={historyLength <= 1}
            aria-label="Undo"
            title="Undo"
            className={`${TOOL_BUTTON} border-gray-200 bg-white hover:bg-gray-100`}
          >
            <span aria-hidden="true">↩️</span>
          </button>
          <button
            type="button"
            onClick={handleClear}
            aria-label="Clear the sheet"
            title="Clear the sheet"
            className={`${TOOL_BUTTON} border-gray-200 bg-white hover:bg-gray-100`}
          >
            <span aria-hidden="true">🗑️</span>
          </button>
        </div>

        <div className="flex min-w-0 flex-1 items-center justify-center bg-gray-300 p-4">
          <canvas
            ref={canvasRef}
            width={CANVAS_WIDTH}
            height={CANVAS_HEIGHT}
            aria-label="Drawing sheet"
            className="block h-auto w-full max-w-[640px] cursor-crosshair touch-none bg-white shadow-md"
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerEnd}
            onPointerCancel={handlePointerEnd}
          />
        </div>

        <div className="flex flex-col border-t border-gray-200 bg-gray-50 [@container_(min-width:720px)]:w-56 [@container_(min-width:720px)]:border-l [@container_(min-width:720px)]:border-t-0">
          <div className="border-b border-gray-200 p-3">
            <p className="mb-2 text-xs font-extrabold text-slate-700">Colors</p>
            <div className="grid grid-cols-12 gap-px">
              {SWATCHES.map((swatch) => (
                <button
                  key={swatch}
                  type="button"
                  onClick={() => pickColor(swatch)}
                  aria-label={`Colour ${swatch}`}
                  aria-pressed={color === swatch}
                  style={{ backgroundColor: swatch }}
                  className={`aspect-square w-full ${
                    color === swatch ? 'relative z-10 ring-2 ring-pink-500 ring-offset-1' : ''
                  }`}
                />
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-2.5 p-3 text-xs font-semibold text-gray-600">
            <p className="text-xs font-extrabold text-slate-700">Brush</p>
            <label className="flex items-center gap-2">
              <span className="w-12">Size</span>
              <input
                type="range"
                min={1}
                max={SIZES.length}
                value={size}
                onChange={(event) => setSize(Number(event.target.value))}
                aria-label="Brush size slider"
                className="min-w-0 flex-1 accent-pink-600"
              />
              <span className="w-6 text-right tabular-nums text-slate-800">{size}</span>
            </label>
            <label className="flex items-center gap-2">
              <span className="w-12">Opacity</span>
              <input
                type="range"
                min={10}
                max={100}
                step={10}
                value={opacity}
                onChange={(event) => setOpacity(Number(event.target.value))}
                aria-label="Brush opacity slider"
                className="min-w-0 flex-1 accent-pink-600"
              />
              <span className="w-6 text-right tabular-nums text-slate-800">{opacity}</span>
            </label>
            <div className="rounded border border-gray-300 bg-white p-2" aria-hidden="true">
              <svg viewBox="0 0 120 40" className="h-10 w-full">
                <path
                  d="M6 26 C 30 4, 50 44, 74 22 S 104 14, 114 20"
                  fill="none"
                  stroke={tool === 'eraser' ? '#d1d5db' : color}
                  strokeWidth={Math.min(size, 14)}
                  strokeLinecap="round"
                  opacity={tool === 'eraser' ? 1 : opacity / 100}
                />
              </svg>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

export default ArtDrawingBoard;
